<template>
    <div>
        <div class="px-3" v-if="showParticipant">
            <person-component
                :person="participant.person"
                :person-search-type="personSearchType"
                :requiredIdentification="true"
                :positionStatIsRequired="true"
                :showExtended="showExtended"
                :showEnNames="showEnNames"
                validate-egn
                @personPopulated="onPersonPopulated"
            ></person-component>

            <BankAccountEditor
                :bankAccount="participant.bankAccount"
                :required="false"
                :showDescription="false"
                :readonly="false"
            ></BankAccountEditor>

            <div class="form-row">
                <div class="form-group col-md-6 col-sm-12">
                    <label
                        class="font-weight-bold required"
                        :for="'participationType' + _uid"
                    >{{$t("registers.events.participants.gridHeaders.participationType")}}</label>
                    <dynamic-dropdown
                        :apiUrl="`/api/participationType/getDropdownModels`"
                        v-model="participant.participationTypeId"
                        :id="'participationType' + _uid"
                        :name="'participationType' + _uid"
                        :required="true"
                        :fieldName="$t('registers.events.participants.gridHeaders.participationType')"
                        :filter="'contains'"
                        :height="500"
                        aria-describedby="participationTypeHelp"
                    ></dynamic-dropdown>
                    <small
                        id="participationTypeHelp"
                        class="form-text text-muted"
                    >{{$t("registers.events.participants.help.participationType")}}</small>
                </div>

                <div class="form-group col-md-6 col-sm-12">
                    <label
                        class="font-weight-bold"
                        :for="'ralationType' + _uid"
                    >{{$t("registers.events.participants.gridHeaders.relationType")}}</label>
                    <dynamic-dropdown
                        ref="participationRelationType"
                        :apiUrl="`/api/participationRelationType/getDropdownModels`"
                        v-model="participant.relationTypeId"
                        :id="'ralationType' + _uid"
                        :name="'ralationType' + _uid"
                        :required="false"
                        :fieldName="$t('registers.events.participants.gridHeaders.relationType')"
                        :filter="'contains'"
                        :height="500"
                        :filterObject="participationRelationTypeFilter"
                        :applyFilterObject="true"
                        :disabled="disabledParticipationRelationType"
                        aria-describedby="relationTypeHelp"
                    ></dynamic-dropdown>
                    <small
                        id="relationTypeHelp"
                        class="form-text text-muted"
                    >{{$t("registers.events.participants.help.relationType")}}</small>
                </div>

                <div class="form-group col-md-6 col-sm-12">
                    <label
                        class="font-weight-bold"
                        :for="'reasonForAdmission' + _uid"
                    >{{$t("registers.events.participants.gridHeaders.reasonForAdmission")}}</label>
                    <dynamic-dropdown
                        ref="reasonForAdmission"
                        :apiUrl="`/api/reasonForAdmission/getDropdownModels`"
                        v-model="participant.reasonForAdmissionId"
                        :id="'reasonForAdmission' + _uid"
                        :name="'reasonForAdmission' + _uid"
                        :required="false"
                        :fieldName="$t('registers.events.participants.gridHeaders.reasonForAdmission')"
                        :filter="'contains'"
                        :height="500"
                        :filterObject="reasonForAdmissionFilter"
                        :applyFilterObject="true"
                        :disabled="disabledReasonForAdmission"
                        aria-describedby="reasonForAdmissionHelp"
                    ></dynamic-dropdown>
                    <small
                        id="reasonForAdmissionHelp"
                        class="form-text text-muted"
                    >{{$t("registers.events.participants.help.reasonForAdmission")}}</small>
                </div>

                <div class="form-group col-md-6 col-sm-12">
                    <label
                        class="font-weight-bold"
                        :for="'participationStatus' + _uid"
                    >{{$t("registers.events.participants.gridHeaders.status")}}</label>
                    <dynamic-dropdown
                        :apiUrl="`/api/participant/participationStatusesDropdownSource`"
                        v-model="participant.statusId"
                        :id="'participationStatus' + _uid"
                        :name="'participationStatus' + _uid"
                        :fieldName="$t('registers.events.participants.gridHeaders.status')"
                        :filter="'contains'"
                        :height="500"
                        aria-describedby="participationStatusHelp"
                    ></dynamic-dropdown>
                    <small
                        id="participationStatusHelp"
                        class="form-text text-muted"
                    >{{$t("registers.events.participants.help.participationStatus")}}</small>
                </div>
            </div>

            <div class="form-group col-12 pl-0 pr-0">
                <label
                    class="font-weight-bold"
                    :for="'note' + _uid"
                >{{$t('registers.events.participants.gridHeaders.note') }}</label>
                <textarea
                    class="form-control"
                    rows="5"
                    v-model="participant.note"
                    :name="'note' + _uid"
                    :id="'note' + _uid"
                ></textarea>
            </div>

            <div class="col-12 pl-0 pr-0">
                <button
                    class="btn btn-link btn-primary ml-0 pl-0"
                    data-toggle="collapse"
                    :href="'#partAddDataCollapse' + _uid"
                    role="button"
                    aria-expanded="false"
                    aria-controls="personDetailsCollapse"
                >
                    <i class="fas fa-caret-down mr-2"></i>
                    {{ $t('home.showMore')}}
                </button>
            </div>

            <div class="col-12 collapse" :id="'partAddDataCollapse' + _uid">
                <div class="row mt-md-2 mt-sm-1">
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isMigrant"
                            :name="'isMigrant' + _uid"
                            :id="'isMigrant' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isMigrant') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isForeigner"
                            :name="'isForeigner' + _uid"
                            :id="'isForeigner' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isForeigner') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isMemberOfAMinority"
                            :name="'isMemberOfAMinority' + _uid"
                            :id="'isMemberOfAMinority' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isMemberOfAMinority') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isOfGypsyOrigin"
                            :name="'isOfGypsyOrigin' + _uid"
                            :id="'isOfGypsyOrigin' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isOfGypsyOrigin') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.withDisabilities"
                            :name="'withDisabilities' + _uid"
                            :id="'withDisabilities' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.withDisabilities') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isHomeless"
                            :name="'isHomeless' + _uid"
                            :id="'isHomeless' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isHomeless') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isDisadvantaged"
                            :name="'isDisadvantaged' + _uid"
                            :id="'isDisadvantaged' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isDisadvantaged') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.isFromUnemployedHousehold"
                            :name="'isFromUnemployedHousehold' + _uid"
                            :id="'isFromUnemployedHousehold' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.isFromUnemployedHousehold') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.withDependentChildren"
                            :name="'withDependentChildren' + _uid"
                            :id="'withDependentChildren' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.withDependentChildren') }}</vs-checkbox>
                    </div>
                    <div class="form-group col-sm-12 col-md-6 col-lg-4">
                        <vs-checkbox
                            v-model="participant.fromOneMemberHousehold"
                            :name="'fromOneMemberHousehold' + _uid"
                            :id="'fromOneMemberHousehold' + _uid"
                            style="justify-content: flex-start !important"
                        >{{ $t('reportData.participants.fromOneMemberHousehold') }}</vs-checkbox>
                    </div>
                </div>
            </div>

            <hr />

            <div class="form-group col-12">
                <file-upload
                    :model="files"
                    :key="'fileUpload' + participant.id"
                    @fileRemoved="fileRemoved"
                ></file-upload>
            </div>
        </div>

        <div class="btn-group">
            <button
                type="button"
                class="btn btn-success"
                v-if="hasEditRights && !showParticipant"
                @click="addParticipant"
            >
                <i class="fas fa-plus"></i>
                {{$t('editor.common.add')}}
            </button>
            <button
                type="button"
                class="btn btn-primary"
                v-if="hasEditRights && showParticipant"
                :disabled="saving"
                @click="saveParticipant"
            >
                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                {{$t('buttons.save.btnText')}}
            </button>
            <button
                type="button"
                class="btn btn-primary"
                v-if="hasEditRights && showParticipant"
                :disabled="saving"
                @click="saveAdnAddNewParticipant"
            >
                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                {{ $t('buttons.saveAdnAddNew') }}
            </button>
            <button
                type="button"
                class="btn btn-danger"
                v-if="hasEditRights && showParticipant"
                :disabled="saving"
                @click="clearParticipant"
            >
                <i class="fas fa-times"></i>
                {{$t('buttons.clear.btnText')}}
            </button>
        </div>
    </div>
</template>

<script>
import PersonComponent from '../../components/person/Person';
import BankAccountEditor from '../../components/bankAccount/BankAccountEditor';
import { Participant, BankAccount } from '../../models/models';
import { PersonSearchType } from '../../models/enums';
import DynamicDropdown from '../../components/dropdown/DynamicDropdown';
import FileUpload from '../../components/common/FileUploadSaveAsTemp';
import participantService from '../../services/participant.service';
import { mapGetters } from 'vuex';

export default {
    inject: ["$validator"],
    props: {
        registerId: {
            type: [Number, String],
            required: true,
        },
        entryId: {
            type: [Number, String],
        },
        personSearchType: {
            type: [Number, String],
            default: PersonSearchType.Person,
        },
        showExtended: {
            type: Boolean,
            default: false,
        },
        showBankAccount: {
            type: Boolean,
            default: false,
        },
        showEnNames: {
            type: Boolean,
            default: false
        }
    },
    components: {
        PersonComponent,
        DynamicDropdown,
        FileUpload,
        BankAccountEditor,
    },
    data() {
        return {
            participant: new Participant(),
            personSearchBoxId: "",
            saving: false,
            showParticipant: false,
            files: [],
        };
    },
    mounted() {},
    methods: {
        addParticipant() {
            this.participant = new Participant({ isManuallyAdded: true });
            this.showParticipant = true;
        },
        clearParticipant() {
            this.participant = new Participant({ isManuallyAdded: true });
            this.files = [];
            this.showParticipant = false;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        },
        saveParticipant() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    kendo
                        .confirm(
                            `${
                                this.create
                                    ? this.$t("person.confirmations.create")
                                    : this.$t("person.confirmations.edit")
                            } '${this.fullName}'?`
                        )
                        .then(
                            function () {
                                this.doSave();
                            }.bind(this)
                        );
                }
            });
        },
        saveAdnAddNewParticipant() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    kendo
                        .confirm(
                            `${
                                this.create
                                    ? this.$t("person.confirmations.create")
                                    : this.$t("person.confirmations.edit")
                            } '${this.fullName}'?`
                        )
                        .then(
                            function () {
                                this.doSave(true);
                            }.bind(this)
                        );
                }
            });
        },
        doSave(addNewAfterSave) {
            this.saving = true;
            this.participant.entryId = this.entryId;
            this.participant.registerId = this.registerId;
            if (Array.isArray(this.files) && this.files.length > 0) {
                let tempFileIds = [];
                this.files.forEach((file) => {
                    tempFileIds.push(file.tempId);
                });

                this.participant.tempFileIds = tempFileIds;
            }

            participantService
                .create(this.participant)
                .then((response) => {
                    this.$emit("save", this.participant);
                    this.clearParticipant();

                    let statusCode = Number(
                        this.$helpers.responseStatusCode(response)
                    );
                    this.$vs.notify({
                        title: this.$t("request.response.status." + statusCode),
                        text: this.$helpers.responseMessage(response),
                        color: statusCode === 240 ? "warning" : "success",
                        time: this.$store.getters.notificationDuration,
                    });

                    if (addNewAfterSave) {
                        this.clearParticipant();
                        this.$nextTick(() => {
                            this.showParticipant = true;
                        });
                    }
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .then(() => { this.saving = false; });
        },
        fileRemoved(fileToRemove) {},
        onPersonPopulated(person) {
            this.participant.bankAccount = new BankAccount(person.bankAccount);
        },
    },
    watch: {},
    computed: {
        ...mapGetters(['isEnVersion', 'participantEditCardRequiredFields']),
        hasEditRights() {
            return true;
        },
        fullName() {
            let name = this.participant.person.firstName;
            if (this.participant.person.middleName) {
                name += ` ${this.participant.person.middleName}`;
            }

            if (this.participant.person.lastName) {
                name += ` ${this.participant.person.lastName}`;
            }

            return name;
        },
        disabledParticipationRelationType() {
            return !this.participant.participationTypeId;
        },
        disabledReasonForAdmission() {
            return !this.participant.participationTypeId;
        },
        participationRelationTypeFilter() {
            return { participationTypeId: this.participant.participationTypeId };
        },
        reasonForAdmissionFilter() {
            return this.participationRelationTypeFilter;
        },
    },
};
</script>

<style scoped>
</style>
